import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { get, includes, isEqual } from 'lodash';
import { Type } from '@lendi/lala-react';

import {
  FIELD_PATHS,
  FIELD_VALUES,
  HYDRATION_STAGES,
  URL_PARAM_APPLICANT_ID,
  URL_PARAM_APPLICATION_ID,
  URL_PARAM_ASSET_ID,
  URL_PARAM_CUSTOMER_ID,
} from '@funnels/constants';
import {
  fetchAllBrokerInterviewsGuarantor,
  fetchApplicants,
  fetchApplications,
  fetchAssets,
  fetchBrokerInterviews,
  fetchCompanies,
  fetchConveyancing,
  fetchCreditReports,
  fetchCustomers,
  fetchEmployments,
  fetchExpenses,
  fetchFinancialSituations,
  fetchIncomes,
  fetchInvitations,
  fetchLead,
  fetchLiabilities,
  fetchPostProductSelect,
  fetchProductPreferences,
  fetchProductSelections,
  fetchRelatedParty,
  fetchRequiredActionConditions,
  resetConveyancing,
} from '@funnels/redux-store';
import { ReduxStoreData, UseParams } from '@funnels/types';
import { getViewType } from '@funnels/utils';

import { useCustomer } from '../useCustomer';
import { useCustomerStoreApplicant } from '../useCustomerApplicant';
import { usePrevious } from '../usePrevious';
import { useRouter } from '../useRouter';

export type Endpoint =
  | 'applications'
  | 'applicants'
  | 'assets'
  | 'brokerCurrentCustomer'
  | 'brokerInterviews'
  | 'brokerInterviewsGuarantor'
  | 'companies'
  | 'conveyancing'
  | 'employments'
  | 'allEmployments'
  | 'expenses'
  | 'incomes'
  | 'invitations'
  | 'liabilities'
  | 'lead'
  | 'financialSituation'
  | 'productPreference'
  | 'creditReports'
  | 'postProductSelect'
  | 'relatedParties'
  | 'productSelections'
  | 'requiredActionConditions';
export type Endpoints = Endpoint[];

function mapState(currentState: ReduxStoreData, isFetchingCustomer: boolean) {
  let loading =
    currentState.applications.loading +
    currentState.applicants.loading +
    currentState.assets.loading +
    currentState.brokerInterviews.loading +
    currentState.companies.loading +
    currentState.conveyancing.loading +
    currentState.employments.loading +
    currentState.expenses.loading +
    currentState.incomes.loading +
    currentState.liabilities.loading +
    currentState.invitations.loading +
    currentState.financialSituations.loading +
    currentState.productPreferences.loading +
    currentState.lead.loading +
    currentState.creditReports.loading +
    currentState.postProductSelect.loading +
    currentState.productSelections.loading +
    currentState.requiredActionConditions.loading +
    currentState.relatedParties.loading;

  if (isFetchingCustomer) {
    loading += currentState.customers.loading;
  }
  return loading;
}

export function useHydrate(endpoints: Endpoints) {
  const dispatch = useDispatch();
  let { [URL_PARAM_CUSTOMER_ID]: customerId } = useParams<UseParams>();
  const {
    [URL_PARAM_APPLICATION_ID]: applicationId,
    [URL_PARAM_APPLICANT_ID]: applicantId,
    [URL_PARAM_ASSET_ID]: assetId,
  } = useParams<UseParams>();
  const { query } = useRouter();
  const { leadid } = query;

  const isFetchingCustomer = endpoints.includes('brokerCurrentCustomer');
  const loading = useSelector((currentState: ReduxStoreData) => mapState(currentState, isFetchingCustomer));
  const storeApplicants = useSelector((currentState: ReduxStoreData) => currentState.applicants.data);
  const isLoading = loading > 0;
  const [stage, setStage] = useState(HYDRATION_STAGES.DRY);
  const prevEndpoints = usePrevious(endpoints);
  const isTeamView = getViewType() === Type.Team;
  const customer = useCustomer();
  const customerApplicant = useCustomerStoreApplicant();

  if (!isTeamView) {
    customerId = customer.data.id;
  }

  useEffect(() => {
    if (isFetchingCustomer && customerId) {
      dispatch(fetchCustomers([customerId]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => {
    // Without any customer id, we shouldn't make any requests at all.
    if (!customerId) {
      return;
    }
    // If the endpoints changes without unmounting the component... maybe an edge case...
    if (!isEqual(prevEndpoints, endpoints)) {
      if (isLoading) {
        setStage(HYDRATION_STAGES.HYDRATING);
      } else {
        setStage(HYDRATION_STAGES.DRY);
      }
    }

    if (includes(endpoints, 'applications')) {
      dispatch(fetchApplications(applicationId, customerId));
    }

    // If no application id && no lead id, no need to even try fetching the other endpoints.
    if (!applicationId && !leadid && !includes(endpoints, 'brokerCurrentCustomer')) {
      setStage(HYDRATION_STAGES.HYDRATED);
      return;
    }

    endpoints.forEach((endpoint: Endpoint) => {
      switch (endpoint) {
        case 'applicants':
          if (applicationId) dispatch(fetchApplicants(applicationId, customerId, 2));
          break;
        case 'assets':
          if (applicationId) dispatch(fetchAssets(applicationId));
          break;
        case 'incomes':
          if (applicationId) dispatch(fetchIncomes(applicationId));
          break;
        case 'invitations':
          if (applicationId) dispatch(fetchInvitations(applicationId));
          break;
        case 'liabilities':
          if (applicationId) dispatch(fetchLiabilities(applicationId));
          break;
        case 'employments':
          // v2
          if (applicationId) {
            if (isTeamView) {
              dispatch(fetchEmployments(applicationId));
            } else {
              const customerApplicantType = get({ applicants: [customerApplicant] }, FIELD_PATHS.APPLICANT_TYPE(0));
              const targetApplicant = storeApplicants.find(
                (applicant) => get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_ID(0)) === applicantId
              );
              const targetApplicantType = get({ applicants: [targetApplicant] }, FIELD_PATHS.APPLICANT_TYPE(0));

              // BORROWER is not permitted to view Guarantor's employment history
              // https://creditandfinance.atlassian.net/wiki/spaces/FT/pages/2575139114/Permission+for+Guarantor+feature
              // This is the only GET request that is called with applicantId, so it needs to be filtered by frontend
              // to avoid 403 errors
              // All the rest are fetchAll in hydration
              const isPermitted = !(
                customerApplicantType === FIELD_VALUES.BORROWER && targetApplicantType === FIELD_VALUES.GUARANTOR
              );

              if (!applicantId || isPermitted) {
                dispatch(fetchEmployments(applicationId, applicantId));
              }
            }
          }
          break;
        case 'allEmployments': // to cater for a situation when all employments are needed in CV but applicantId presents
          // v2
          if (applicationId) {
            dispatch(fetchEmployments(applicationId, undefined));
          }
          break;
        case 'companies':
          if (applicationId) {
            dispatch(fetchCompanies(applicationId));
          }
          break;
        case 'expenses':
          if (applicationId) dispatch(fetchExpenses(applicationId));
          break;
        case 'lead':
          if (leadid) dispatch(fetchLead(leadid));
          break;
        case 'financialSituation':
          if (applicationId) dispatch(fetchFinancialSituations(applicationId));
          break;
        case 'productPreference':
          if (applicationId) dispatch(fetchProductPreferences(applicationId));
          break;
        case 'creditReports':
          if (applicationId) dispatch(fetchCreditReports(applicationId));
          break;
        case 'postProductSelect':
          if (applicationId) dispatch(fetchPostProductSelect(applicationId));
          break;
        case 'relatedParties':
          if (applicationId) dispatch(fetchRelatedParty(applicationId));
          break;
        case 'brokerInterviews':
          if (applicationId) dispatch(fetchBrokerInterviews(applicationId));
          break;
        case 'brokerInterviewsGuarantor':
          if (applicationId) dispatch(fetchAllBrokerInterviewsGuarantor(applicationId));
          break;
        case 'productSelections':
          if (applicationId) dispatch(fetchProductSelections(applicationId));
          break;
        case 'requiredActionConditions':
          if (applicationId) dispatch(fetchRequiredActionConditions(applicationId));
          break;
        case 'conveyancing':
          if (customerId) {
            if (assetId) {
              dispatch(fetchConveyancing({ customerId, property: { id: assetId } }));
            } else {
              dispatch(resetConveyancing());
            }
          }
          break;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, leadid, JSON.stringify(endpoints), customerId]);
  useEffect(() => {
    if (stage === HYDRATION_STAGES.DRY && isLoading) {
      setStage(HYDRATION_STAGES.HYDRATING);
    } else if (stage === HYDRATION_STAGES.HYDRATING && !isLoading) {
      setStage(HYDRATION_STAGES.HYDRATED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return stage;
}
